import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { CredentialsService } from '@app/auth';
import { Draw } from '../models/lottery.model';
import { environment } from '@env/environment';
import { HttpService } from './http.service';
import { firstValueFrom, Observable } from 'rxjs';
import { doc, docData } from '@angular/fire/firestore';
import { ResultLotenal, SalesDay, SalesPerGameData } from '../models/sorteo.model';
import { GeneralService } from './general.service';

@Injectable({
  providedIn: 'root',
})
export class LotenalService {
  private collectionName = 'lotenal-purchases';
  private collectionLogos = 'lotenal-logos';
  private collectionSales = 'salesPerDay'; // Para guardar las ventas por día
  private collectionSalesGame = 'salesPerGame'; // Para guardar las ventas por día de cada sorteo
  private collectionResults = 'resultsLotenal'; // Para traer los resultados de los sorteos
  private refCounter = 1;
  constructor(
    private firestore: AngularFirestore,
    private credentialsS: CredentialsService,
    private httpS: HttpService,
    private generalS?: GeneralService
  ) {}

  async savePurchase(
    lnResponseCR: any,
    lnRequestCR: any,
    drawType: string,
    codEstado: number,
    drawActual: any
  ): Promise<string> {
    const foliosVenta: string[] = [];
    const codVerificacionPremio: string[] = [];

    const billetes = lnResponseCR?.lnResponseCR?.billeteConfirmado;

    if (Array.isArray(billetes)) {
      billetes.forEach((billete: any) => {
        foliosVenta.push(billete?.folioVenta ?? '');
        codVerificacionPremio.push(billete?.codVerificacionPremio ?? '');
      });
    }

    await this.saveSalesPerDay(lnResponseCR, drawType);
    const actual: Draw[] = [];
    const drawsArray = Array.isArray(drawActual) ? drawActual : [drawActual];
    const cleanedDraws = drawsArray.map((draw) => ({
      ...draw,
      signos: Array.isArray(draw.signos) ? draw.signos : [],
    }));

    const localDate = new Date();
    const localISOString = new Date(localDate.getTime() - localDate.getTimezoneOffset() * 60000).toISOString();

    if (cleanedDraws.length > 2) {
      const found = cleanedDraws.find((draw) => draw.numeroSorteo === lnResponseCR.numeroSorteo);
      if (found) {
        actual.push(found);
      }
    } else {
      actual.push(...cleanedDraws);
    }
    const purchaseData = {
      drawActual: actual.length > 0 ? actual : null,
      request: lnRequestCR || {},
      response: lnResponseCR || {},
      customerId: this.credentialsS.credentials?.id || '',
      createdAt: localISOString,
      drawType: drawType || '',
      codEstado: codEstado || 0,
      signos: [],
      foliosVenta: foliosVenta,
      codVerificacionPremio: codVerificacionPremio,
    };
    const cleanForFirestore = (obj: any): any => {
      if (obj === undefined) return null;
      if (obj === null) return null;
      if (typeof obj !== 'object') return obj;

      if (Array.isArray(obj)) {
        return obj.map((item) => cleanForFirestore(item));
      }

      const cleaned: any = {};
      for (const key in obj) {
        cleaned[key] = cleanForFirestore(obj[key]);
      }
      return cleaned;
    };

    const firestoreData = cleanForFirestore(purchaseData);

    try {
      const docRef = await this.firestore.collection(this.collectionName).add(firestoreData);
      return docRef.id;
    } catch (error) {
      // Aqui va el endpoint de cancelar compra
      this.cancelPurchase(lnResponseCR, drawType);
      console.error('Error al guardar la compra:', error);
      throw error;
    }
  }

  async saveSalesPerDay(draw: any, drawType: string) {
    try {
      const tickets = draw.lnResponseCR.billeteConfirmado;
      if (!Array.isArray(tickets) || tickets.length === 0) return;

      const docId = this.getSalesDocId();
      const docRef = this.firestore.collection(this.collectionSales).doc(docId);
      const docSnapshot = await firstValueFrom(docRef.get());

      let totalFracciones = 0;
      let totalImporte = 0;
      const nuevosFolios = [];

      for (const ticket of tickets) {
        totalFracciones += ticket.cantFracciones;
        totalImporte += ticket.importe;

        // Con estos datos ya es poi¿sible generar el CSV en el cms
        nuevosFolios.push({
          ov: environment.userLotenal.ov,
          idTerminal: this.credentialsS.credentials?.id || '',
          codVerificacionPremio: ticket.codVerificacionPremio,
          folioVenta: ticket.folioVenta,
          cantFracciones: ticket.cantFracciones,
          importe: ticket.importe,
          numeroSorteo: draw.lnResponseCR.numeroSorteo,
          tipoSorteo: draw.lnResponseCR.tipoSorteo,
          fechaConfirmacion: draw.lnResponseCR.fechaConfirmacion,
        });
      }
      //console.log('nuevosFolios: ', nuevosFolios);
      if (docSnapshot.exists) {
        const existingData = docSnapshot.data() as SalesDay;
        const updatedFolios = [...(existingData.folios || []), ...nuevosFolios];

        await docRef.update({
          cantFracciones: (existingData.cantFracciones || 0) + totalFracciones,
          importe: (existingData.importe || 0) + totalImporte,
          folios: updatedFolios,
          totalVentas: updatedFolios.length,
        });
      } else {
        await docRef.set({
          cantFracciones: totalFracciones,
          importe: totalImporte,
          folios: nuevosFolios,
          totalVentas: nuevosFolios.length,
        });
      }

      await this.saveSalesPerGame(
        docId,
        drawType,
        totalFracciones,
        totalImporte,
        nuevosFolios.length,
        draw.lnResponseCR.numeroSorteo,
        draw.lnResponseCR.tipoSorteo
      );
    } catch (error) {
      //console.log('Error al guardar en ventas por dia: ', error);
    }
  }

  async saveSalesPerGame(
    docId: string,
    drawType: string,
    fracciones: number,
    importe: number,
    totalVentas: number,
    numeroSorteo: number,
    tipoSorteo: number
  ) {
    try {
      const docRef = this.firestore.collection(this.collectionSalesGame).doc(docId);
      const docSnapshot = await firstValueFrom(docRef.get());
      const sorteoKey = numeroSorteo.toString();

      let updatedData: any = {
        ov: environment.userLotenal.ov,
        idTerminal: this.credentialsS.credentials?.id || '',
        tipoSorteo,
        cantFracciones: fracciones,
        importe: importe,
        totalVentas: totalVentas,
      };

      if (docSnapshot.exists) {
        const existingData = docSnapshot.data() as SalesPerGameData;
        const juegosPorTipo = existingData[drawType] || {};
        const juegoActual = juegosPorTipo[sorteoKey] || {
          ov: environment.userLotenal.ov,
          idTerminal: this.credentialsS.credentials?.id || '',
          tipoSorteo,
          cantFracciones: 0,
          importe: 0,
          totalVentas: 0,
        };

        // sumamos valores acumulados
        updatedData = {
          ov: environment.userLotenal.ov,
          idTerminal: this.credentialsS.credentials?.id || '',
          tipoSorteo,
          cantFracciones: juegoActual.cantFracciones + fracciones,
          importe: juegoActual.importe + importe,
          totalVentas: juegoActual.totalVentas + totalVentas,
        };
      }

      await docRef.set(
        {
          [drawType]: {
            [sorteoKey]: updatedData,
          },
        },
        { merge: true } // <- esto es CLAVE
      );
    } catch (error) {
      //console.log('Error al guardar en ventas por juego: ', error);
    }
  }

  async cancelPurchase(draw: any, drawType: string) {
    try {
      const url = `${environment.api.tradicional.purchases}/cancelPurchaseNotIssued`;

      for (const billete of draw?.lnResponseCR?.billeteConfirmado) {
        const idPrefix = this.credentialsS?.credentials?.id.slice(0, 4);
        const counter = await this.generalS?.counterRefCode();
        const textCounter = `${idPrefix}${counter}`;
        const body = {
          lnRequestCA: {
            requestHeadTerminal: {
              // idTerminal: 1234567890123,
              idTerminal: this.credentialsS.credentials?.id || '',
              ov: environment.userLotenal.ov,
              usuario: environment.userLotenal.user,
              password: environment.userLotenal.password,
              refCode: `cancel-purchase-not-issued-${drawType}-${textCounter}`,
            },
            codVerificacionPremio: billete.codVerificacionPremio,
            folioVenta: billete.folioVenta,
          },
        };

        try {
          let response = await this.httpS.post(url, body);
          //console.log('Cancelación exitosa');
        } catch (error) {
          // console.log(`Error al cancelar billete con folioVenta: ${billete.folioVenta}`, error);
        }
      }
    } catch (error) {
      // console.log('Error al liberar los boletos: ', error);
    }
  }

  //Traer cachitos y series de bd
  async getAllLogos(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.firestore
        .collection<any>(this.collectionLogos)
        .valueChanges({ idField: 'id' })
        .subscribe({
          next: (data) => resolve(data),
          error: (err) => reject(err),
        });
    });
  }

  async releaseTickets(idTransaccion: number, drawNumber: number) {
    try {
      if (idTransaccion === 0 || !drawNumber) {
        //console.log(`No se ejecuta releaseTickets porque idTransaccion es 0 (drawNumber: ${drawNumber})`);
        return;
      }

      const url = `${environment.api.tradicional.reservation}/releaseTickets`;
      const idPrefix = this.credentialsS?.credentials?.id.slice(0, 4);
      const counter = await this.generalS?.counterRefCode();
      const textCounter = `${idPrefix}${counter}`;
      const body = {
        lnRequestLB: {
          requestHeadTerminal: {
            idTerminal: this.credentialsS.credentials?.id || '',
            ov: environment.userLotenal.ov,
            usuario: environment.userLotenal.user,
            password: environment.userLotenal.password,
            refCode: `release-tickets-${drawNumber}-${textCounter}`,
          },
          idTransaccion: idTransaccion,
        },
      };

      const response = (await this.httpS.post(url, body)) as any;
      // console.log('liberando: ', response, drawNumber);
    } catch (error) {
      // console.log('Error releasing tickets: ', error);
    }
  }

  // Función reutilizable para obtener el docId ajustado al corte horario 22:00 a 21:59
  getSalesDocId(): string {
    // Obtener la hora local (ajustada a tu zona horaria, ej. 'America/Mexico_City')
    const localTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Mexico_City' }));
    const localHours = localTime.getHours();
    //console.log('Hora actual:', localTime.toISOString(), 'Hora local:', localTime.getHours());
    // Formatear fecha en YYYY-MM-DD (usando la fecha local, no UTC)
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Si son las 22:00 hrs o más (hora local), usar mañana
    if (localHours >= 22) {
      const tomorrow = new Date(localTime);
      tomorrow.setDate(localTime.getDate() + 1);
      return formatDate(tomorrow);
    }

    // Si no, usar la fecha actual local
    return formatDate(localTime);
  }

  // Traer resultados de premios mayores y vendidos
  getResultByGameId(gameId: string): Observable<ResultLotenal | undefined> {
    const docRef = this.firestore.collection<ResultLotenal>(this.collectionResults).doc(gameId);
    return docRef.valueChanges();
  }
  // Actualizar resultados de premios mayores y vendidos
  saveResultByGameId(gameId: string, data: any) {
    return this.firestore.collection('resultsLotenal').doc(gameId).set(data, { merge: true });
  }
}
